import type { ComponentType } from 'react';
import type { Control } from 'react-hook-form';
import { EventCompetitivenessEnum, SportsEnum, TeamTypesEnum } from 'types/generated/client';
import GameTypeAny from 'svg/GameTypeAny';
import GameTypeCasual from 'svg/GameTypeCasual';
import GameTypeCompetitive from 'svg/GameTypeCompetitive';
import GameTypeDrill from 'svg/GameTypeDrill';
import GameTypeMatchPrep from 'svg/GameTypeMatchPrep';
import GenderAll from 'svg/GenderAll';
import GenderFemale from 'svg/GenderFemale';
import GenderMale from 'svg/GenderMale';
import { GameGender, type VenueAddressWithCity } from 'screens/ListGame/types';
import type { SkillLevelRangeFormValues } from 'components/Find/SkillLevelRangeField';

export { SELF_RATE_RATING_SYSTEM, SkillLevelMode } from 'components/Find/SkillLevelRangeField';

/** Rendered order matters: `utils.ts` derives the row order + labels from this enum. */
export enum Weekday {
  Sun = 'SUN',
  Mon = 'MON',
  Tue = 'TUE',
  Wed = 'WED',
  Thu = 'THU',
  Fri = 'FRI',
  Sat = 'SAT',
}

/** "Choose up to 3 recurring time slots." */
export const MAX_AVAILABILITY_SLOTS = 3;

export interface AvailabilitySlot {
  day: Weekday;
  /** Native time input values (HH:mm, 24h); the slot recurs weekly. */
  startTime: string;
  endTime: string;
}

/** "Preferred gender of playing partner" — Any maps to the same DB value as All. */
export const PARTNER_GENDER_OPTIONS: { label: string; value: GameGender }[] = [
  { label: 'Men', value: GameGender.Men },
  { label: 'Women', value: GameGender.Women },
  { label: 'Any', value: GameGender.All },
];

/** Single-select; a player can hold one active availability per sport + game type. */
export const GAME_TYPE_OPTIONS: { label: string; value: EventCompetitivenessEnum }[] = [
  { label: 'Any', value: EventCompetitivenessEnum.Any },
  { label: 'Recreational play', value: EventCompetitivenessEnum.Casual },
  { label: 'Competitive', value: EventCompetitivenessEnum.Competitive },
  { label: 'Match prep', value: EventCompetitivenessEnum.MatchPrep },
  { label: 'Drill', value: EventCompetitivenessEnum.Drill },
];

/** The game-type line in the preview's "Looking for" section. */
export const GAME_TYPE_SUMMARY_LABELS: Record<EventCompetitivenessEnum, string> = {
  [EventCompetitivenessEnum.Any]: 'Any kind of game',
  [EventCompetitivenessEnum.Casual]: 'Recreational play',
  [EventCompetitivenessEnum.Competitive]: 'Competitive play',
  [EventCompetitivenessEnum.MatchPrep]: 'Match preparation',
  [EventCompetitivenessEnum.Drill]: 'Drills',
};

export const GAME_TYPE_SUMMARY_ICONS: Record<
  EventCompetitivenessEnum,
  ComponentType<{ className?: string }>
> = {
  [EventCompetitivenessEnum.Any]: GameTypeAny,
  [EventCompetitivenessEnum.Casual]: GameTypeCasual,
  [EventCompetitivenessEnum.Competitive]: GameTypeCompetitive,
  [EventCompetitivenessEnum.MatchPrep]: GameTypeMatchPrep,
  [EventCompetitivenessEnum.Drill]: GameTypeDrill,
};

export interface ListAvailabilityFormValues extends SkillLevelRangeFormValues {
  sport: SportsEnum;
  matchType: TeamTypesEnum;
  /** The "Looking for a regular partner" toggle. */
  isLookingForRegularPartner: boolean;
  competitiveness: EventCompetitivenessEnum;
  gender: GameGender;
  /** Recurring weekly availability — up to MAX_AVAILABILITY_SLOTS days. */
  availability: AvailabilitySlot[];
  description: string;
  /** Preferred neighborhoods (ids from the `neighborhoods` table); empty = none listed. */
  neighborhoodIds: string[];
  /** The "Preferred courts" toggle; off clears `venueIds` (any court). */
  hasPreferredCourts: boolean;
  /** Preferred venues — multi-select, unlike a game's single venue. */
  venueIds: string[];
}

export interface AvailabilityScheduleFieldProps {
  control: Control<ListAvailabilityFormValues>;
}

export interface PreferredLocationsFieldProps {
  value: string[];
  onChange: (venueIds: string[]) => void;
  /** Full details of the selected venues — the preview step needs more than the ids. */
  onVenuesSelected?: (venues: VenueAddressWithCity[]) => void;
  /**
   * Venues to seed the slider with (edit mode) so currently-selected venues that
   * aren't in the nearby geo results still show and stay highlighted.
   */
  initialVenues?: VenueAddressWithCity[];
}

export interface PreviewStepProps {
  values: ListAvailabilityFormValues;
  venues: VenueAddressWithCity[];
  /** Display names for `values.neighborhoodIds`, in list order. */
  neighborhoodNames: string[];
  onGoBack: () => void;
  onPublish: () => void;
  isPublishing?: boolean;
  /** "DUPR 3 - 5" / "Intermediate - Advanced" — the title's rating half. */
  skillSummary?: string;
  /** The final CTA label — "Publish" when creating, "Save changes" when editing. */
  publishLabel?: string;
}

/** The gender line in the preview's "Looking for" section. */
export const GENDER_LOOKING_FOR_LABELS: Record<GameGender, string> = {
  [GameGender.All]: 'Looking to play with anyone',
  [GameGender.Men]: 'Looking to play with men',
  [GameGender.Women]: 'Looking to play with women',
  [GameGender.Nonbinary]: 'Looking to play with nonbinary players',
};

export const GENDER_LOOKING_FOR_ICONS: Record<GameGender, ComponentType<{ className?: string }>> = {
  [GameGender.All]: GenderAll,
  [GameGender.Men]: GenderMale,
  [GameGender.Women]: GenderFemale,
  [GameGender.Nonbinary]: GenderAll,
};

/** The gender-preference line in the preview's "Looking for" section. */
export const GENDER_PLAYER_LABELS: Record<GameGender, string> = {
  [GameGender.All]: 'All players',
  [GameGender.Men]: 'Male player',
  [GameGender.Women]: 'Female player',
  [GameGender.Nonbinary]: 'Nonbinary player',
};
